<!-- eslint-disable vue/no-v-html -->
<template>
<NuxtImg class="feature-blur-bg" src="/images/howtoplay/campus1.original.jpeg" alt="" aria-hidden />
<div class="persona-container">
    <div class="content padded">
        <NuxtLink
            v-slot:default="{navigate}"
            :to="'/how-to-play'"
        >
            <div class="breadcrumb" aria-hidden v-on:click="navigate">
                <NuxtImg
                    class="fa fa-regular fa-circle-left"
                    :alt="$t('NAVBAR.BACK')"
                    aria-hidden
                    focusable="false"
                />
                <span class="back-text">{{ $t('HOW_TO_PLAY.HEADER') }}</span>
            </div>
        </NuxtLink>
        <NuxtImg
            class="featured" src="/images/howtoplay/campus1.original.jpeg"
            alt="An auditorium of students playing Jackbox Games"
        />
        <div class="intro" v-html="content?.content" />
        <div>
            <div class="email-signup-container">
                <div class="email-signup">
                    <EmailSignup
                        subscriber-id="studentsNews"
                        :data-fields="[{ name: 'language', id: '4242', value: listrakLocale }]"
                        :button-text="$t('FORM.SUBMIT')"
                    />
                </div>
            </div>
        </div>
        <div class="sections">
            <div class="quip-bullets">
                <div class="quip-bullet">
                    <NuxtImg src="/images/howtoplay/quip1.png" alt="Quip 1" />
                    <h2 v-text="sections.q1?.title" />
                </div>
                <div v-html="sections.q1?.content" />
                <div class="quip-bullet">
                    <NuxtImg src="/images/howtoplay/quip2.png" alt="Quip 2" />
                    <h2 v-text="sections.q2?.title" />
                </div>
                <div v-html="sections.q2?.content" />
                <div class="quip-bullet">
                    <NuxtImg src="/images/howtoplay/quip3.png" alt="Quip 3" />
                    <h2 v-text="sections.q3?.title" />
                </div>
                <div v-html="sections.q3?.content" />
            </div>
        </div>
    </div>
    <div class="getting-started">
        <div class="content">
            <h1 v-text="sections.intro?.title" />
            <div v-html="sections.intro?.content" />
            <div class="sections">
                <div class="section">
                    <NuxtImg src="/images/howtoplay/purchasing.png" alt="digital purchase" />
                    <h2 v-text="sections.purchasing?.title" />
                    <div v-html="sections.purchasing?.content" />
                </div>
                <div class="section">
                    <NuxtImg src="/images/howtoplay/support.png" alt="Jackbox support" />
                    <h2 v-text="sections.support?.title" />
                    <div v-html="sections.support?.content" />
                </div>
                <div class="section">
                    <NuxtImg src="/images/howtoplay/settings.png" alt="settings" />
                    <h2 v-text="sections.settings?.title" />
                    <div v-html="sections.settings?.content" />
                </div>
            </div>
        </div>
    </div>
    <div class="content padded">
        <div class="sections">
            <div class="flex-row reverse">
                <div class="flex-column right">
                    <h2 v-text="sections.conclusion?.title" />
                    <div v-html="sections.conclusion?.content" />
                    <a href="/how-to-play">
                        <ButtonElement
                            id="submitButton"
                            class="outline"
                        >
                            {{ $t('HOW_TO_PLAY.WATCH_TUTORIAL') }}
                        </ButtonElement>
                    </a>
                </div>
                <div class="flex-column">
                    <NuxtImg src="/images/howtoplay/im-in.png" alt="Raised hands - I'm in." />
                </div>
            </div>
        </div>
    </div>
</div>
</template>

<script lang="ts">
import { I18n } from '$services/i18n'
import EmailSignup from '$components/forms/EmailSignup.vue'
import ButtonElement from '$components/ButtonElement.vue'
import type { JBGWeb } from '$types/JBGWeb'

export default defineNuxtComponent({
    components: {
        EmailSignup,
        ButtonElement
    },
    fetchKey() {
        const { $i18n } = useNuxtApp()
        return `jackbox-for-students-${$i18n.locale.value}`
    },
    data() {
        return {
            isError: false,
            isLoading: true,
            content: <JBGWeb.GenericContent|null> null,
            sections: <{[key: string]: JBGWeb.GenericSection}> {}
        }
    },
    async asyncData({ $cms, $i18n }) {
        const data = {}

        try {
            const currentLocale = $i18n.locale.value
            const localeContentKey = (currentLocale !== 'en')
                ? `jackbox-for-students-${currentLocale}`
                : 'jackbox-for-students'

            const content = await $cms.fetchOne<JBGWeb.GenericContent>('generic-contents', localeContentKey, {
                populate: ['sections'],
                locale: $i18n.locale.value
            })

            data.content = content
            if (data.content && data.content.sections) {
                data.sections = data.content.sections.reduce((
                    map: {[key: string]: JBGWeb.GenericSection},
                    obj: JBGWeb.GenericSection
                ) => {
                    map[obj.key] = obj
                    return map
                }, {})
            }
        } catch (error) {
            console.error('Failed to fetch Jackbox for Students content:', error.message)
            data.isError = true
        } finally {
            data.isLoading = false
        }

        return data
    },
    head({ $i18n }) {
        return {
            title: `${$i18n.t('HOW_TO_PLAY.FOR_STUDENTS')}`,
            meta: [
                {
                    name: 'description',
                    content: this.content?.seo?.metaDescription
                }
            ],
            link: [
                {
                    rel: 'canonical',
                    href: 'https://www.jackboxgames.com/jackbox-for-students'
                },
                {
                    rel: 'alternate',
                    hreflang: 'x-default',
                    href: 'https://www.jackboxgames.com/jackbox-for-students'
                },
                {
                    rel: 'alternate',
                    hreflang: 'en',
                    href: 'https://www.jackboxgames.com/jackbox-for-students'
                },
                {
                    rel: 'alternate',
                    hreflang: 'fr',
                    href: 'https://www.jackboxgames.com/fr/jackbox-for-students'
                },
                {
                    rel: 'alternate',
                    hreflang: 'it',
                    href: 'https://www.jackboxgames.com/it/jackbox-for-students'
                },
                {
                    rel: 'alternate',
                    hreflang: 'de',
                    href: 'https://www.jackboxgames.com/de/jackbox-for-students'
                },
                {
                    rel: 'alternate',
                    hreflang: 'es-ES',
                    href: 'https://www.jackboxgames.com/es/jackbox-for-students'
                },
                {
                    rel: 'alternate',
                    hreflang: 'es',
                    href: 'https://www.jackboxgames.com/es/jackbox-for-students'
                },
                {
                    rel: 'alternate',
                    hreflang: 'es-MX',
                    href: 'https://www.jackboxgames.com/es-XL/jackbox-for-students'
                },
                {
                    rel: 'alternate',
                    hreflang: 'pt-BR',
                    href: 'https://www.jackboxgames.com/pt-BR/jackbox-for-students'
                }
            ]
        }
    },
    computed: {
        listrakLocale() {
            return I18n.getLocaleForListrak(this.$i18n.locale)
        }
    }
})
</script>

<style lang="scss">
@use "$styles/kit.scss" as *;

.feature-blur-bg {
    position: fixed;
    z-index: 0;
    width: 100%;
    filter: blur(8px);
    opacity: 0.3;
    @include mq-medium-and-more {
            margin-top: -120px;
    }
    @include mq-small-and-less {
        margin-top: -75px;
    }
}
.persona-container {
    position: relative;
    z-index: 1;

    .breadcrumb {
        color: var(--neutral-100);
        margin-bottom: 10px;
        .back-text {
            padding-left: 5px;
        }
        &:hover {
            color: var(--primary-200);
        }
    }

    .content {
        margin-left: auto;
        margin-right: auto;

        @include mq-large {
            max-width: 900px;
        }
        @include mq-medium {
            max-width: 900px;
        }
        .quip-bullet {
            display: flex;
            flex-direction: row;
            gap: 20px;
            align-items: center;
        }
        .sections {
            display: flex;
            flex-direction: column;
            gap: 40px;
        }
        .flex-row {
            width: 100%;
            display: flex;
            flex-direction: row;
            gap: 40px;
            align-items: center;
            @include mq-xsmall {
                flex-direction: column;
                &.reverse {
                    flex-direction: column-reverse;
                }
            }
            .right {
                text-align: right;
                @include mq-xsmall {
                    text-align: left;
                }

                ul {
                    text-align: left;
                    padding-left: 10px;
                    li {
                        margin-bottom: 20px;
                    }
                }
            }
            .flex-column {
                flex: 1;
                width: 50%;
                @include mq-xsmall {
                    width: 80%;
                }
                img {
                    border-radius: 32px;
                    box-shadow: 0px 9px 46px 8px rgba(0, 0, 0, 0.12),
                        0px 24px 38px 3px rgba(0, 0, 0, 0.14),
                        0px 11px 15px 0px rgba(0, 0, 0, 0.20);
                }
                img.simple {
                    border-radius: unset;
                    box-shadow: unset;
                }
            }
        }
    }
    .featured {
        border-radius: 20px;
        width: 100%;
    }

    .email-signup {
        margin: auto;
        max-width: 400px;
    }
}
.getting-started {
    .content {
        max-width: 1100px;
        padding: 15px;
        @include mq-small-and-less {
            padding: 15px 30px 15px 30px;
        }
        .sections {
            display: flex;
            flex-direction: row;
            gap: 15px;
            color: var(--neutral-100);
            @include mq-small-and-less {
                flex-direction: column;
            }
            .section {
                flex: 1;
                padding: 20px;
                border-radius: 20px;
                border: 1px solid var(--neutral-600);
                background: var(--neutral-700);
                text-align: center;
                img {
                    width: 100%;
                    max-width: 300px;
                }
                p, ul, li {
                    text-align: left;
                    font-size: 15px;
                }
            }
        }
    }
}

</style>
